<template>
  <div v-if="inspectionTemplate" class="EditInspectionTemplate" :class="{ archived: inspectionTemplate.archived }">
    <iconToggle
      :value="inspectionTemplate.archived"
      iconOff="static/icon/H2BOON-list-missing.png"
      iconOn="static/icon/H2BOON-list-plus.png"
      class="right"
      @input="updateSimpleProperty('archived', $event)"
    />

    <input-container class="title-input full px-4 m-0 mt-4">
      <translated-field
        :value="inspectionTemplate.title"
        :label="$t('Title')"
        :readonly="inspectionTemplate.archived"
        @input="updateSimpleProperty('title', $event)"
      />
    </input-container>

    <div class="mx-4 my-3">
      <p>{{ $t('editInspectionSections.line1') }}</p>
      <p>{{ $t('editInspectionSections.line2') }}</p>
      <p>{{ $t('editInspectionSections.line3') }}</p>
    </div>

    <b-custom-switch
      v-if="hasArchivedSections"
      :value="showArchived"
      :label="$t('Show Removed Sections')"
      style="float: right"
      @input="showArchived = $event"
    />

    <div v-if="hasWarnings" class="m-4 warnings-list">
      <div v-for="warning of warnings" class="warnings-list-item p-4 d-flex flex-direction-column align-items-center">
        <b-icon font-size="1.5em" icon="exclamation-triangle-fill" variant="warning" class="mr-4" />
        <div>{{ warning }}</div>
      </div>
    </div>

    <list-builder
      :label="$t('Failure reason list')"
      :items="inspectionTemplate.failureOptions"
      class="m-4 p-4 failure-reason-list"
      @update="onFailureReasonUpdate"
      @delete="onFailureReasonDelete"
      @new="onFailureReasonNew"
    />

    <list-builder
      :label="$t('Priorities list')"
      :items="inspectionTemplate.priorities"
      class="m-4 p-4 priorities-list"
      @update="onPriorityUpdate"
      @new="onPriorityNew"
    />

    <div class="items">
      <draggable :value="sections" handle=".glyphicon-move" class="section-list" @update="onSectionMoved">
        <template v-for="section in sections">
          <div
            v-if="showArchived || !section.archived"
            :key="section._id"
            :class="['section-icon', { archived: section.archived }]"
          >
            <b-icon icon="grip-vertical" class="glyphicon-move" style="float: right" />
            <h1>
              <b-icon :icon="section.icon || 'card-text'" />
              &nbsp;
              <locale-text :text="section.title" />
            </h1>

            <ul class="section-items">
              <template v-for="item in section.items">
                <li v-if="!item.archived" :key="item._id" style="list-style-type: none">
                  <b-icon :icon="item.icon || 'dot'" />
                  <locale-text :text="item.title" />
                </li>
              </template>
            </ul>
            <div class="section-menu">
              <router-link
                :to="{
                  name: 'EditInspectionSection',
                  params: {
                    _id: projectId,
                    inspectionTemplateId,
                    inspectionSectionId: section._id,
                  },
                }"
              >
                <div class="section-menu-contents">
                  {{ $t('Edit') }}
                </div>
              </router-link>
            </div>
          </div>
          <div v-else :key="section._id + '-hidden'"></div>
        </template>

        <div class="section-icon" key="new">
          {{ $t('New section') }}
          <div class="section-menu">
            <div class="section-menu-contents" @click="createInspectionSection">
              {{ $t('Create') }}
            </div>
          </div>
        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import draggable from 'vuedraggable';

import TranslatedField from '@/components/form/TranslatedField';
import inputContainer from '@/components/form/inputContainer';
import iconToggle from '@/components/iconToggle/iconToggle';
import BCustomSwitch from '@/components/organisation/BCustomSwitch';
import LocaleText from '@/components/common/LocaleText';
import ListBuilder from '@/components/common/ListBuilder.vue';

export default {
  name: 'EditInspectionTemplate',

  metaInfo() {
    return {
      title: this.title,
    };
  },

  components: {
    draggable,
    iconToggle,
    TranslatedField,
    inputContainer,
    BCustomSwitch,
    LocaleText,
    ListBuilder,
  },

  props: {
    inspectionTemplateId: {
      type: String,
      default: null,
    },
    projectId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      showArchived: false,
    };
  },

  computed: {
    ...mapGetters(['currentProjectInspectionTemplatesById']),

    inspectionTemplate() {
      return this.currentProjectInspectionTemplatesById(this.inspectionTemplateId);
    },

    title() {
      return this.inspectionTemplate
        ? this.$t('Edit Inspection Template {title}', { title: this.inspectionTemplate.title })
        : '';
    },

    sections() {
      return this.inspectionTemplate ? this.inspectionTemplate.sections : [];
    },

    itemCount() {
      return this.sections.length;
    },

    hasArchivedSections() {
      return this.sections && this.sections.some(({ archived }) => !!archived);
    },

    warnings() {
      const items = [];
      if (!this.inspectionTemplate) {
        return items;
      }

      if (!this.inspectionTemplate.failureOptions || !this.inspectionTemplate.failureOptions.length) {
        items.push(this.$t('Failure reason list is empty'));
      }
      if (!this.inspectionTemplate.priorities || !this.inspectionTemplate.priorities.length) {
        items.push(this.$t('Priorities list is empty'));
      }

      return items;
    },

    hasWarnings() {
      return !!this.warnings.length;
    },
  },

  methods: {
    ...mapActions(['saveInspectionTemplateProperties', 'saveInspectionSectionProperties']),

    onSectionMoved(e) {
      this.saveInspectionSectionProperties({
        updateInspectionTemplate: this.inspectionTemplate,
        sectionIndex: e.oldIndex,
        newIndex: e.newIndex,
        reason: 'Moved Section',
      });
    },

    updateSimpleProperty(property, newValue) {
      if (this.inspectionTemplate[property] !== newValue) {
        this.saveInspectionTemplateProperties({
          updateInspectionTemplate: this.inspectionTemplate,
          newInspectionTemplateProperties: { [property]: newValue },
          reason: `Edit property ${property}`,
        });
      }
    },

    async createInspectionSection() {
      const section = await this.saveInspectionSectionProperties({
        updateInspectionTemplate: this.inspectionTemplate,
        sectionIndex: this.itemCount,
        newInspectionTemplateProperties: {},
        reason: 'Created new section',
      });

      this.$router.push({
        name: 'EditInspectionSection',
        params: {
          _id: this.projectId,
          inspectionTemplateId: this.inspectionTemplate._id,
          inspectionSectionId: section._id,
        },
      });
    },

    onFailureReasonUpdate(item) {
      const items = [...(this.inspectionTemplate.failureOptions || [])];
      const index = items.findIndex(({ key }) => item.key === key);
      items.splice(index, index >= 0 ? 1 : 0, item);
      this.updateSimpleProperty('failureOptions', items);
    },

    onFailureReasonDelete(key) {
      const items = [...(this.inspectionTemplate.failureOptions || [])].filter((item) => item.key !== key);
      this.updateSimpleProperty('failureOptions', items);
    },

    onFailureReasonNew(item) {
      const items = [...(this.inspectionTemplate.failureOptions || []), item];
      this.updateSimpleProperty('failureOptions', items);
    },

    onPriorityUpdate(item) {
      const items = [...(this.inspectionTemplate.priorities || [])];
      const index = items.findIndex(({ key }) => item.key === key);
      items.splice(index, index >= 0 ? 1 : 0, item);
      this.updateSimpleProperty('priorities', items);
    },

    onPriorityDelete(key) {
      const items = [...(this.inspectionTemplate.priorities || [])].filter((item) => item.key !== key);
      this.updateSimpleProperty('priorities', items);
    },

    onPriorityNew(item) {
      const items = [...(this.inspectionTemplate.priorities || []), item];
      this.updateSimpleProperty('priorities', items);
    },
  },
};
</script>

<style scoped>
.EditInspectionTemplate {
  margin-bottom: 3em;
  margin-top: 22px;
  padding: 20px;
}

.title-input {
  font-size: 1.75rem;
}

img.icon {
  width: 25px;
  margin-right: 5px;
}

.archived {
  color: grey;
}

.section-list {
  width: 100%;
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  align-items: center;
  align-content: space-around;
  justify-content: center;
  padding-top: 30px;
}

.section-icon {
  width: 15vw;
  height: 10vw;
  color: white;
  padding: 10px;
  margin: 10px;
  text-align: center;
  position: relative;
  border: 2px solid #343a40;
  border-radius: 10px;
  background: #ffffff05;
  overflow: hidden;
}
.section-icon h1 {
  font-size: 100%;
}

.section-icon.archived {
  background: #91000040;
}
.section-icon.archived h1 {
  text-decoration: line-through;
}

.glyphicon-move {
  color: white;
  float: left;
  position: relative;
  z-index: 10;
}

.section-icon:hover .section-menu {
  display: block;
}

.section-menu {
  display: none;
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 10px;
}

.section-menu-contents {
  top: 50%;
  position: relative;
  line-height: 17px;
  transform: translateY(-50%);
  padding: 15px;
  cursor: pointer;
}

.section-items {
  font-size: 8pt;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.failure-reason-list,
.priorities-list {
  border: 1px solid white;
  border-radius: 5px;
}

.warnings-list {
  border: 1px solid var(--bs-warning);
  border-radius: 5px;
}

.warnings-list-item {
  border-bottom: 1px solid var(--bs-warning);
}

.warnings-list-item:last-child {
  border-bottom: none;
}
</style>



// WEBPACK FOOTER //
// src/components/project/EditInspectionTemplate.vue